import { z } from 'zod';
import { uuidv7 } from 'uuidv7';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo } from 'react';
import { TransactionStatus, TransactionType } from '../api/transaction-types';
import { useBudgetId } from '../../budget/budget-store';
import { useCreateTransactionApi } from '../api/use-create-transaction';
/**
 * Schema for adjustment form validation
 */
export const adjustmentFormSchema = z.object({
    offBudgetAccountId: z.string().uuid({ message: 'Please select an off-budget account' }),
});
/**
 * Hook to manage adjustment form state and submission
 * Creates an inverse transaction on the selected off-budget account
 */
export const useAdjustmentForm = ({ transaction, onSuccess }) => {
    const budgetId = useBudgetId();
    /**
     * Creates default values for the adjustment form
     *
     * @returns Default form data with empty off-budget account selection
     */
    const createDefaultValues = useCallback(() => {
        return {
            offBudgetAccountId: '',
        };
    }, []);
    const defaultValues = useMemo(() => createDefaultValues(), [createDefaultValues]);
    const form = useForm({
        resolver: zodResolver(adjustmentFormSchema),
        defaultValues,
    });
    /**
     * Handles successful form submission
     */
    const handleSuccess = useCallback(() => {
        if (onSuccess) {
            onSuccess();
        }
    }, [onSuccess]);
    const { mutate: mutateCreate, isPending: isCreatePending } = useCreateTransactionApi({
        onSuccess: handleSuccess,
    });
    /**
     * Creates the inverse transaction data for the off-budget account
     *
     * @param values - Form values containing the selected off-budget account
     * @returns TransactionRequest for the inverse transaction
     */
    const createInverseTransaction = useCallback((values) => {
        const amount = transaction.inflow || transaction.outflow || 0;
        const isOriginalInflow = !!transaction.inflow;
        // Calculate the same timestamp as the original transaction to keep them linked
        const dateEpoch = transaction.dateEpoch;
        return {
            id: uuidv7(),
            budgetId,
            accountId: values.offBudgetAccountId,
            type: TransactionType.TRANSACTION,
            status: TransactionStatus.PENDING,
            payee: transaction.payee || '',
            dateEpoch,
            inflow: isOriginalInflow ? 0 : amount,
            outflow: isOriginalInflow ? amount : 0,
            bucketId: undefined, // Off-budget accounts don't use buckets
        };
    }, [transaction, budgetId]);
    /**
     * Handles form submission by creating the inverse transaction
     */
    const handleSubmit = useCallback((values) => {
        const inverseTransaction = createInverseTransaction(values);
        mutateCreate(inverseTransaction);
    }, [createInverseTransaction, mutateCreate]);
    return {
        form,
        onSubmit: form.handleSubmit(handleSubmit),
        isSubmitting: isCreatePending,
    };
};
